export type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'silent';

// Default disabled tags - add tags here to disable them by default
// Note: Strip the trailing colon from tag constants when adding to the Set
const DEFAULT_DISABLED_TAGS = new Set<string>([
  // Uncomment to disable DateRangePicker debug logs:
  // 'DateRangePicker',
  // 'PLANNER_PAGINATION_LOG_TAG',
]);

export interface LoggerOptions {
  level?: LogLevel;
  pretty?: boolean;
  disabled?: boolean;
  destination?:
    | ((level: LogLevel, ...args: any[]) => void)
    | Array<(level: LogLevel, ...args: any[]) => void>;
}

const levelOrder: Record<LogLevel, number> = {
  trace: 10,
  debug: 20,
  info: 30,
  warn: 40,
  error: 50,
  silent: 100,
};

function consoleDestination(level: LogLevel, ...args: any[]) {
  if (level === 'debug') console.debug(...args);
  else if (level === 'info') console.info(...args);
  else if (level === 'warn') console.warn(...args);
  else if (level === 'error') console.error(...args);
  else if (level === 'trace') console.debug(...args);
}

function prettyFormat(level: LogLevel, ...args: any[]) {
  const color: Record<LogLevel, string> = {
    trace: '\x1b[90m', // gray,
    debug: '\x1b[36m', // cyan
    info: '\x1b[32m', // green
    warn: '\x1b[33m', // yellow
    error: '\x1b[31m', // red
    silent: '',
  };
  const reset = '\x1b[0m';
  return [`${color[level]}[${level.toUpperCase()}]${reset}`, ...args];
}

export class Logger {
  private level: LogLevel;
  private pretty: boolean;
  private disabled: boolean;
  private destinations: Array<(level: LogLevel, ...args: any[]) => void>;

  constructor(options: LoggerOptions = {}) {
    this.level = options.level ?? 'debug';
    this.pretty = options.pretty ?? false;
    this.disabled = options.disabled ?? false;
    if (Array.isArray(options.destination)) {
      this.destinations =
        options.destination.length > 0 ? options.destination : [consoleDestination];
    } else if (options.destination) {
      this.destinations = [options.destination];
    } else {
      this.destinations = [consoleDestination];
    }
  }

  setLevel(level: LogLevel) {
    this.level = level;
  }

  setDisabled(disabled: boolean) {
    this.disabled = disabled;
  }

  setDestination(
    dest:
      | ((level: LogLevel, ...args: any[]) => void)
      | Array<(level: LogLevel, ...args: any[]) => void>
  ) {
    if (Array.isArray(dest)) {
      this.destinations = dest.length > 0 ? dest : [consoleDestination];
    } else {
      this.destinations = [dest];
    }
  }

  private shouldLog(level: LogLevel, tag?: string) {
    if (tag && DEFAULT_DISABLED_TAGS.has(tag)) {
      return false;
    }
    return !this.disabled && levelOrder[level] >= levelOrder[this.level] && this.level !== 'silent';
  }

  private log(level: LogLevel, ...args: any[]) {
    // Check if first arg is a tag (ends with ':')
    const firstArg = args[0];
    const isTagged = typeof firstArg === 'string' && firstArg.endsWith(':');

    if (isTagged) {
      const tag = firstArg.slice(0, -1);
      if (!this.shouldLog(level, firstArg)) return;
      const logArgs = this.pretty
        ? prettyFormat(level, `[${tag}]`, ...args.slice(1))
        : [`[${tag}]`, ...args.slice(1)];
      for (const dest of this.destinations) {
        dest(level, ...logArgs);
      }
    } else {
      if (!this.shouldLog(level)) return;
      const logArgs = this.pretty ? prettyFormat(level, ...args) : args;
      for (const dest of this.destinations) {
        dest(level, ...logArgs);
      }
    }
  }

  trace(...args: any[]) {
    this.log('trace', ...args);
  }
  debug(...args: any[]) {
    this.log('debug', ...args);
  }
  info(...args: any[]) {
    this.log('info', ...args);
  }
  warn(...args: any[]) {
    this.log('warn', ...args);
  }
  error(...args: any[]) {
    this.log('error', ...args);
  }
}

export const logger = new Logger({
  level: import.meta.env.VITE_ENV === 'prod' ? 'silent' : 'trace',
  pretty: true,
});
